'use client';

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import useGetAccountFolders from 'src/hooks/accountFolders/useGetAccountFolders';
import { useGetFilterSetsQuery } from 'src/api/redux/filterSets';
import { useAnalytics } from 'src/hooks/analytics';

export const useCodeSetDropdown = (location?: string) => {
  const {
    selectedCodeSet = [],
    volumeTypes = [],
    setSelectedCodeSet,
    clearSelectedCodeSet,
    removeVolumeConfig,
  } = useAnalytics(location);

  // Referring volume isn't reported by site, so a code set can't be added in
  // Referring mode while Sites is the selected volume type. The Sites option is
  // disabled in the reverse direction by AnalyticsTableHeader.
  const isReferringDisabled = volumeTypes.includes('sites');

  const { data: filterSetsData, isLoading: isFilterSetsLoading } =
    useGetFilterSetsQuery({
      serializer: 'detailed',
    });

  const { data: foldersData, isLoading: isFoldersLoading } =
    useGetAccountFolders();

  const [isOpen, setIsOpen] = useState(false);
  const [expandedFolder, setExpandedFolder] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<string | null>(
    'company'
  );
  const dropdownPositionRef = useRef<{
    top: number;
    left: number;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const organizedData = useMemo(() => {
    if (!filterSetsData || !foldersData) return { sections: [], data: {} };

    const data = {};
    const sectionMap = {};

    const filterSetsMap = new Map();
    filterSetsData.results.forEach((item) => {
      filterSetsMap.set(item.id, item);
    });

    foldersData.results.forEach((folder) => {
      const originalScope = folder.scope || 'Unknown';
      const scopeKey = originalScope.toLowerCase();

      if (!data[scopeKey]) {
        data[scopeKey] = { folders: [], ungroupedItems: [] };
        sectionMap[scopeKey] = originalScope;
      }

      const folderFilterSets = folder.filter_sets
        .filter((item) => item.type === 'CODE')
        .map((item) => filterSetsMap.get(item.id) || item)
        .filter(Boolean);

      const folderItems = folderFilterSets.sort((a, b) =>
        a.label.localeCompare(b.label)
      );

      data[scopeKey].folders.push({
        ...folder,
        items: folderItems,
      });
    });

    Object.keys(data).forEach((scopeKey) => {
      data[scopeKey].folders.sort((a, b) => a.label.localeCompare(b.label));
    });

    const codeSets = filterSetsData?.results?.filter(
      (item) => item.type === 'CODE'
    );

    const itemsInFolders = new Set();
    foldersData.results.forEach((folder) => {
      folder.filter_sets.forEach((item) => {
        itemsInFolders.add(item.id);
      });
    });

    codeSets.forEach((item) => {
      if (!itemsInFolders.has(item.id)) {
        const originalScope = item.scope || 'Unknown';
        const scopeKey = originalScope.toLowerCase();

        if (!data[scopeKey]) {
          data[scopeKey] = { folders: [], ungroupedItems: [] };
          sectionMap[scopeKey] = originalScope;
        }

        data[scopeKey].ungroupedItems.push(item);
      }
    });

    Object.keys(data).forEach((scopeKey) => {
      data[scopeKey].ungroupedItems.sort((a, b) =>
        a.label.localeCompare(b.label)
      );
    });

    const sortedSections = Object.keys(data)
      .sort((a, b) => {
        if (a === 'company' && b !== 'company') return -1;
        if (b === 'company' && a !== 'company') return 1;
        return a.localeCompare(b);
      })
      .map((key) => sectionMap[key]);

    return {
      sections: sortedSections,
      data: data,
    };
  }, [filterSetsData, foldersData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = useCallback(() => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      dropdownPositionRef.current = {
        top: rect.bottom + 4,
        left: rect.left,
      };
    }
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleItemSelection = useCallback(
    (item: any) => {
      const instanceId = `${item.id}_${crypto.randomUUID()}`;
      setSelectedCodeSet([{ ...item, instanceId, mode: 'treating' }]);
      setIsOpen(false);
    },
    [setSelectedCodeSet]
  );

  const handleClearSelection = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      clearSelectedCodeSet();
    },
    [clearSelectedCodeSet]
  );

  const getCodeSetInstanceCount = useCallback(
    (itemId: string) => {
      return selectedCodeSet.filter((cs: any) => cs.id === itemId).length;
    },
    [selectedCodeSet]
  );

  const handleModeToggle = useCallback(
    (item: any, mode: 'treating' | 'sending') => {
      const existingInstance = selectedCodeSet.find(
        (cs: any) => cs.id === item.id && cs.mode === mode
      );

      if (existingInstance) {
        const updatedCodeSets = selectedCodeSet.filter(
          (cs: any) => !(cs.id === item.id && cs.mode === mode)
        );
        setSelectedCodeSet(updatedCodeSets);
        if (existingInstance.instanceId) {
          removeVolumeConfig(existingInstance.instanceId);
        }
      } else {
        // Guard the action as well as the checkbox, so a stale Sites selection
        // can't be combined with Referring. Removal above stays available so an
        // existing Referring code set can always be unselected.
        if (mode === 'sending' && isReferringDisabled) return;
        if (selectedCodeSet.length >= 10) return;
        const existingInstances = selectedCodeSet.filter(
          (cs: any) => cs.id === item.id
        );
        if (existingInstances.length >= 2) return;

        const instanceId = `${item.id}_${crypto.randomUUID()}`;
        const itemWithInstanceId = { ...item, instanceId, mode };
        setSelectedCodeSet([...selectedCodeSet, itemWithInstanceId]);
      }
    },
    [
      selectedCodeSet,
      setSelectedCodeSet,
      removeVolumeConfig,
      isReferringDisabled,
    ]
  );

  const getActiveModesForCodeSet = useCallback(
    (itemId: string): ('treating' | 'sending')[] => {
      return selectedCodeSet
        .filter((cs: any) => cs.id === itemId)
        .map((cs: any) => cs.mode || 'treating');
    },
    [selectedCodeSet]
  );

  const toggleFolder = useCallback((folderId: string) => {
    setExpandedFolder((prev) => (prev === folderId ? null : folderId));
  }, []);

  const toggleSection = useCallback((sectionKey: string) => {
    setExpandedSections((prev) => (prev === sectionKey ? null : sectionKey));
  }, []);

  const hasValue = selectedCodeSet.length > 0;
  const isLoading = isFilterSetsLoading || isFoldersLoading;

  const label = useMemo(() => {
    if (selectedCodeSet.length === 0) return 'Select Code Set...';
    if (selectedCodeSet.length === 1) return selectedCodeSet[0].label;
    return `${selectedCodeSet.length} Code Sets Selected`;
  }, [selectedCodeSet]);

  return {
    // State
    selectedCodeSet,
    isOpen,
    isLoading,
    hasValue,
    label,
    isReferringDisabled,
    dropdownPosition: dropdownPositionRef.current,
    organizedData,
    expandedFolder,
    expandedSections,

    // Refs
    dropdownRef,
    buttonRef,

    // Actions
    toggleDropdown,
    handleItemSelection,
    handleClearSelection,
    handleModeToggle,
    getCodeSetInstanceCount,
    getActiveModesForCodeSet,
    toggleFolder,
    toggleSection,
  };
};
